import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { Browser } from '@capacitor/browser';

/**
 * Check if the app is running as a native mobile app
 */
export const isNativePlatform = (): boolean => {
  try {
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();
    console.log('[NativePlugins] Platform detection:', { isNative, platform });
    return isNative;
  } catch (error) {
    console.log('[NativePlugins] Platform detection error:', error);
    return false;
  }
};

/**
 * Get the current platform (ios, android, or web)
 */
export const getPlatform = (): 'ios' | 'android' | 'web' => {
  try {
    const platform = Capacitor.getPlatform();
    if (platform === 'ios' || platform === 'android') {
      return platform;
    }
    return 'web';
  } catch {
    return 'web';
  }
};

/**
 * Initialize native plugins when running as a mobile app
 * This should be called once on app startup
 */
export const initializeNativePlugins = async (): Promise<void> => {
  // Skip initialization on web browsers
  if (!isNativePlatform()) {
    return;
  }

  const platform = getPlatform();

  try {
    // CRITICAL: Prevent status bar from overlaying WebView content
    await StatusBar.setOverlaysWebView({ overlay: false });
    
    // Configure StatusBar with GoDyspo dark theme - Dark style = white/light icons for dark backgrounds
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#080c14' });

    // Configure Keyboard behavior for better form UX
    if (platform === 'ios') {
      await Keyboard.setAccessoryBarVisible({ isVisible: true });
      await Keyboard.setScroll({ isDisabled: false });
    }

    // Hide splash screen after initialization
    await SplashScreen.hide();
  } catch (error) {
    // Gracefully handle any plugin initialization errors
    console.warn('Native plugin initialization failed:', error);
  }
};

/**
 * Set status bar to dark background with white icons
 * Dark style = white/light icons for dark backgrounds
 */
export const setDarkBackgroundStatusBar = async (): Promise<void> => {
  if (!isNativePlatform()) return;
  
  try {
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: '#080c14' });
  } catch (error) {
    console.warn('Failed to set status bar color:', error);
  }
};

/**
 * Opens an external URL in the appropriate browser
 * - Native: Uses Capacitor Browser plugin (opens system browser)
 * - Web: Uses window.open
 */
export const openExternalUrl = async (url: string): Promise<void> => {
  if (isNativePlatform()) {
    try {
      await Browser.open({ url });
    } catch (error) {
      console.warn('Failed to open browser, falling back to window.open:', error);
      window.open(url, '_blank');
    }
  } else {
    window.open(url, '_blank');
  }
};
